import axios from "axios";
import { isAnnualPricingPlanDuration } from "../api/services/pricing-plan.billing";
import type { PricingPlanWithFeatures } from "../api/services/pricing-plan.type";

/** Maps stock analysis category names (API) to pricing-plan feature keys. */
export const STOCK_CATEGORY_FEATURE_KEYS: Record<string, string> = {
  "Big Stocks": "big_stock_analyses",
  "Cheap Stocks": "cheap_stock_analyses",
  Investors: "investor_stock_analyses",
};

export interface ReportAccessLocationState {
  ticket?: string;
  company?: string;
  categoryName?: string;
  categoryId?: string;
}

export interface ReportDetailLinkParams {
  reportId: string;
  analysisId: string;
  categoryId?: string;
  ticket?: string;
  company?: string;
}

/** Builds `/report-detail/:id?...` so lock screens work on shared/direct links. */
export const buildReportDetailPath = ({
  reportId,
  analysisId,
  categoryId,
  ticket,
  company,
}: ReportDetailLinkParams): string => {
  const params = new URLSearchParams();
  if (analysisId) params.set("analysisId", analysisId);
  if (categoryId) params.set("category", categoryId);
  if (ticket) params.set("ticket", ticket);
  if (company) params.set("company", company);
  const query = params.toString();
  return `/report-detail/${reportId}${query ? `?${query}` : ""}`;
};

/**
 * Parses category name from API 402 bodies like
 * `{ error: "Access denied to Big Stocks" }`.
 */
export const parseAccessDeniedCategory = (error: unknown): string | null => {
  if (!axios.isAxiosError(error)) return null;
  const data = error.response?.data as
    | { error?: string; message?: string }
    | undefined;
  const raw = `${data?.error || ""} ${data?.message || ""}`.trim();
  if (!raw) return null;

  const deniedMatch = raw.match(/Access denied to\s+(.+?)(?:\.|$)/i);
  if (deniedMatch?.[1]) return deniedMatch[1].trim();

  // Fall back to known category names mentioned in the error text.
  for (const categoryName of Object.keys(STOCK_CATEGORY_FEATURE_KEYS)) {
    if (raw.toLowerCase().includes(categoryName.toLowerCase())) {
      return categoryName;
    }
  }

  return null;
};

export const getFeatureKeyForStockCategory = (
  categoryName: string | null | undefined,
): string | null => {
  if (!categoryName) return null;
  return STOCK_CATEGORY_FEATURE_KEYS[categoryName] ?? null;
};

/**
 * Returns the cheapest monthly plan that includes the given feature
 * (e.g. Standard for Big Stocks, Premium for Cheap Stocks).
 */
export const getRequiredPlanNameForFeature = (
  featureKey: string | null | undefined,
  plans: PricingPlanWithFeatures[] | undefined,
): string | null => {
  if (!featureKey || !plans?.length) return null;

  const monthlyPlans = plans
    .filter((p) => !isAnnualPricingPlanDuration(p.plan.duration))
    .sort((a, b) => Number(a.plan.price) - Number(b.plan.price));

  for (const planWithFeatures of monthlyPlans) {
    const feature = planWithFeatures.feature_list?.find(
      (f) => f.feature_key === featureKey,
    );
    if (feature?.value === "true") {
      return planWithFeatures.plan.name;
    }
  }

  return null;
};

export const getRequiredPlanNameForCategory = (
  categoryName: string | null | undefined,
  plans: PricingPlanWithFeatures[] | undefined,
): string | null => {
  return getRequiredPlanNameForFeature(
    getFeatureKeyForStockCategory(categoryName),
    plans,
  );
};
